/**
 * Archetype copy — single source of truth (fetched from backend).
 *
 * The literal copy used to live here as static exports; now it comes from
 * ``GET /api/archetypes/copy`` so backend + frontend stay in sync forever.
 * This module keeps the shape of the previous exports and adds:
 *
 *   • ``loadArchetypeCopy()``    — eager preload (call once from App root).
 *   • ``useArchetypeCopy()``     — React hook returning ``{loading, copy}``.
 *   • ``getComboFrom(copy,d,s)`` — pure helper (no side effects).
 *   • ``getShadowFrom(copy,a)``  — pure helper.
 *
 * A module-level promise+cache ensures we fetch **at most once per session**.
 */
import { useEffect, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let _cache = null;   // parsed payload
let _inflight = null; // Promise while a fetch is running

export async function loadArchetypeCopy() {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = fetch(`${API}/archetypes/copy`)
    .then((r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    })
    .then((data) => {
      _cache = data;
      _inflight = null;
      return _cache;
    })
    .catch((e) => {
      _inflight = null;
      throw e;
    });
  return _inflight;
}

export function useArchetypeCopy() {
  const [state, setState] = useState({
    loading: !_cache,
    copy: _cache,
  });
  useEffect(() => {
    if (_cache) {
      // Already cached — keep state.copy in sync (Fast Refresh case).
      setState({ loading: false, copy: _cache });
      return;
    }
    let mounted = true;
    loadArchetypeCopy()
      .then((copy) => {
        if (mounted) setState({ loading: false, copy });
      })
      .catch(() => {
        if (mounted) setState({ loading: false, copy: null });
      });
    return () => { mounted = false; };
  }, []);
  return state;
}

export function getComboFrom(copy, dominant, secondary) {
  if (!copy || !dominant || !secondary || dominant === secondary) return null;
  return copy.combos?.[dominant]?.[secondary] || null;
}

export function getShadowFrom(copy, archetype) {
  if (!copy || !archetype) return null;
  return copy.shadow_mode?.[archetype] || null;
}
